import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Clock, CheckCircle2, Plus, FileSearch, Download, Target } from 'lucide-react';
import '@/App.css';

// CRITICAL: Use environment variable for backend URL - NO HARDCODING
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const [apiStatus, setApiStatus] = useState('checking');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0
  });

  useEffect(() => {
    checkApiConnection();
    fetchStats();
  }, []);

  const checkApiConnection = async () => {
    try {
      const response = await axios.get(`${API}/health`, { 
        timeout: 5000,
        withCredentials: false // Disable credentials to avoid CORS issues
      });
      console.log('API Health Check:', response.data);
      setApiStatus('connected');
    } catch (error) {
      console.error('API connection error:', error);
      setApiStatus('disconnected');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/inspections/stats`, {
        withCredentials: false
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Quick action handlers with explicit event handling
  const handleNewInspection = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('New Inspection clicked');
    navigate('/inspections/new');
  };

  const handleViewPending = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('View Pending clicked');
    navigate('/inspections?filter=pending');
  };

  const handleGenerateReport = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Generate Report clicked');
    navigate('/reports/generate');
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold">SurvWork</h1>
          </div>
          <nav className="flex gap-2">
            <Button variant="default" size="sm" data-testid="nav-dashboard">
              Dashboard
            </Button>
            <Button variant="ghost" size="sm" data-testid="nav-inspections" onClick={() => navigate('/inspections')}>
              Inspections
            </Button>
            <Button variant="ghost" size="sm" data-testid="nav-reports" onClick={() => navigate('/reports')}>
              Reports
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" data-testid="dashboard-title">Dashboard</h2>
          <div className="flex items-center gap-2">
            <p className="text-slate-400">Welcome to SurvWork Inspection Engine</p>
            <Badge 
              variant={apiStatus === 'connected' ? 'success' : 'destructive'}
              className="ml-4"
              data-testid="api-status"
            >
              {apiStatus === 'checking' ? 'Checking...' : apiStatus === 'connected' ? 'API: Connected' : 'API: Disconnected'}
            </Badge>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-slate-800 border-slate-700" data-testid="stat-total">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.total}</p>
                  <p className="text-sm text-slate-400">Total Inspections</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700" data-testid="stat-pending">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-slate-400">Pending Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700" data-testid="stat-completed">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.completed}</p>
                  <p className="text-sm text-slate-400">Completed Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions - CRITICAL: Explicit event handlers with proper button type */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              type="button"
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-3 bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-blue-500 transition-all"
              onClick={handleNewInspection}
              data-testid="quick-action-new-inspection"
            >
              <Plus className="w-8 h-8 text-blue-400" />
              <span className="text-lg">New Inspection</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-3 bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-blue-500 transition-all"
              onClick={handleViewPending}
              data-testid="quick-action-view-pending"
            >
              <FileSearch className="w-8 h-8 text-blue-400" />
              <span className="text-lg">View Pending</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-3 bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-blue-500 transition-all"
              onClick={handleGenerateReport}
              data-testid="quick-action-generate-report"
            >
              <Download className="w-8 h-8 text-blue-400" />
              <span className="text-lg">Generate Report</span>
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-16 py-4">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500">
          SurvWork Inspection Engine v1.0
        </div>
      </footer>
    </div>
  );
};

// Inspections List Component
const Inspections = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <h2 className="text-2xl font-bold mb-4" data-testid="inspections-title">Inspections</h2>
      <p className="text-slate-400 mb-4">View and manage all inspections</p>
      <Button type="button" onClick={() => navigate('/')} data-testid="back-to-dashboard">
        Back to Dashboard
      </Button>
    </div>
  );
};

// New Inspection Component
const NewInspection = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <h2 className="text-2xl font-bold mb-4" data-testid="new-inspection-title">New Inspection</h2>
      <p className="text-slate-400 mb-4">Create a new property inspection</p>
      <Button type="button" onClick={() => navigate('/')} data-testid="back-to-dashboard">
        Back to Dashboard
      </Button>
    </div>
  );
};

// Reports Component
const Reports = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <h2 className="text-2xl font-bold mb-4" data-testid="reports-title">Reports</h2>
      <p className="text-slate-400 mb-4">Generate and view inspection reports</p>
      <Button type="button" onClick={() => navigate('/')} data-testid="back-to-dashboard">
        Back to Dashboard
      </Button>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inspections" element={<Inspections />} />
          <Route path="/inspections/new" element={<NewInspection />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/generate" element={<Reports />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;